import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import Breadcrumbs from "../../../components/Common/Breadcrumbs/AntdBreadcrumbs";
import DoctorsFilterOptions from "../../../components/Common/CommonFilterOptions";
import NavBarTitles from "../../../components/Common/TabBar";
import Deletepopup from "../../../components/Modal/Deletepopup";

// Updated custom hook paths
import Actions from "../../../components/Common/Actions";
import FilledButton from "../../../components/Common/Buttons/FilledButton";
import ListPageLayout from "../../../components/Layout";
import useDebouncedSearch from "../../../components/Utils/hooks/useDebouncedSearch";
import useDeleteHandler from "../../../components/Utils/hooks/useDeleteHandler";
import usePageChange from "../../../components/Utils/hooks/usePageChange";
import usePaginatedData from "../../../components/Utils/hooks/usePaginatedData";
import useSortHandler from "../../../components/Utils/hooks/useSortHandler";

export const DoctorCommonHeads = [
  {
    name: "Gender",
    navigatePath: "/doctors-gender",
  },
  {
    name: "Experience",
    navigatePath: "/doctors-experience",
  },
  {
    name: "Languages Known",
    navigatePath: "/doctors-language",
  },
  {
    name: "Medical Council",
    navigatePath: "/doctors-medical-council",
  },
  {
    name: "Clinic Speciality",
    navigatePath: "/doctors-clinic-speciality",
  },
  {
    name: "Online Specialization",
    navigatePath: "/online-specialization",
  },
  {
    name: "Inclinic Specialization",
    navigatePath: "/inclinic-specializations",
  },
];

const InclinicSpecialization = () => {
  const navigate = useNavigate();
  const addNewInclinicSpecialization = {
    id: "addnewpost",
    onClick: () => navigate("/inclinic-specialization-create"),
    style: { width: "240px" },
    label: "Add New Inclinic Specialization",
  };
  // Sorting
  const [sort, setSort] = useState("");
  const [sortName, setSortName] = useState("Newest First");
  const handleSortClick = useSortHandler(setSort, setSortName);
  const [loader, setLoader] = useState(true);

  // Search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedSearch(setSearch, () =>
    setPagination((prev) => ({ ...prev, skip: 0, currentPage: 1 })),
  );

  // Delete state
  const [deleteId, setDeleteId] = useState("");
  const [showDelete, setShowDelete] = useState(false);

  // Pagination
  const { records, totalCount, pagination, setPagination, fetchData } =
    usePaginatedData({
      apiUrl: `${process.env.REACT_APP_API_URL}inclinic-specialization/list`,
      initialParams: {
        sort,
        searchText: search,
      },
    });

  const handlePageChange = usePageChange(pagination, setPagination);
  const handleDelete = useDeleteHandler(
    "inclinic-specialization",
    fetchData,
    setShowDelete,
  );

  useEffect(() => {
    setLoader(true)
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.skip, pagination.limit, sort, search]);

  const columns = [
    {
      title: "S.No",
      render: (text, record, index) =>
        Number(pagination.skip || 0) + (index + 1),
      key: "serial",
      width: 80,
      fixed: "left",
    },
    {
      title: "Inclinic Specialization",
      dataIndex: "inc_specialization",
      key: "inc_specialization",
      fixed: "left",
    },
    {
      title: "Status",
      width: 150,
      key: "status",
      render: (text, record, index) => (record.active ? "Active" : "InActive"),
    },
    {
      title: "Actions",
      key: "actions",
      width: 350,
      render: (_, record) => (
        <Actions
          isEdit={true}
          isView={true}
          isDelete={true}
          onClickView={() =>
            navigate(`/inclinic-specialization-view/${record._id}`)
          }
          onClickDelete={() => {
            setDeleteId(record._id);
            setShowDelete(true);
          }}
          onClickEdit={() =>
            navigate(`/inclinic-specializations-edit/${record._id}`)
          }
        />
      ),
    },
  ];

  const dataSource = records.map((el) => ({ ...el, key: el._id }));

  const breadcrumbsList = useMemo(
    () => [
      {
        text: "Doctor",
        link: "/add-new-doctors",
      },
      {
        text: "Common Heads",
        link: "/inclinic-specializations",
      },
      {
        text: "Inclinic Specialization",
        link: "",
      },
    ],
    [],
  );

  useEffect(() => {
    records && setTimeout(() => setLoader(false));
  }, [records]);
  return (
    <ListPageLayout
      breadcrumbs={
        <Breadcrumbs
          className=" healthsyMasterBreadCrumbs"
          breadcrumbsList={breadcrumbsList}
        />
      }
      addButton={<FilledButton props={addNewInclinicSpecialization} />}
      titles={<NavBarTitles titles={DoctorCommonHeads} />}
      filters={
        <DoctorsFilterOptions
          limit={pagination.limit}
          setLimit={(val) =>
            setPagination((prev) => ({
              ...prev,
              limit: val,
              skip: 0,
              currentPage: 1,
            }))
          }
          search={search}
          onChange={debouncedSearch}
          handleSortClick={handleSortClick}
          sortName={sortName}
          filter_records={() => {}}
          // setFilterShow={() => setIsFilterModalVisible(true)}
          showFilter={false}
        />
      }
      tableProps={{
        columns,
        dataSource,
      }}
      totalCount={totalCount}
      pagination={pagination}
      onPageChange={(selected) => handlePageChange({ selected })}
      loader={loader}
    >
      {/* Optional children like modals */}
      {showDelete && (
        <Deletepopup
          handleDelete={() => handleDelete(deleteId)}
          id={deleteId}
          handleClose={() => setShowDelete(false)}
        />
      )}
    </ListPageLayout>
  );
};

export default InclinicSpecialization;
